/**
 * Split a string into alternating non-match / match segments so the
 * caller can wrap matched substrings in `<mark>` (or any other tag)
 * to show users WHY a suggestion matched their query.
 *
 * Returns an array of `{ text, isMatch }` objects in the original
 * source order. Matching is case-insensitive.
 *
 * Tokens shorter than 2 chars are dropped (too noisy: single letters
 * would highlight half the title).
 *
 * Example:
 *   splitMatches("Disneyland Paris", ["disney", "paris"])
 *   → [
 *       { text: "Disney",     isMatch: true  },
 *       { text: "land ",      isMatch: false },
 *       { text: "Paris",      isMatch: true  },
 *     ]
 */
export function splitMatches(text, tokens) {
  if (text == null || text === "") return [];
  const str = String(text);
  if (!Array.isArray(tokens) || tokens.length === 0) {
    return [{ text: str, isMatch: false }];
  }
  const cleaned = tokens
    .map((t) => String(t || "").trim())
    .filter((t) => t.length >= 2)
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  if (cleaned.length === 0) {
    return [{ text: str, isMatch: false }];
  }
  // Longest token first so "disneyland" matches before "disney" — we
  // don't want to fragment "Disneyland" into "Disney" + "land".
  cleaned.sort((a, b) => b.length - a.length);
  const re = new RegExp(`(${cleaned.join("|")})`, "gi");
  // String.split with a capture group keeps the captures interleaved
  // at odd indices.
  const parts = str.split(re);
  const out = [];
  for (let i = 0; i < parts.length; i++) {
    const piece = parts[i];
    if (!piece) continue;
    out.push({ text: piece, isMatch: i % 2 === 1 });
  }
  return out;
}

/**
 * Tokenize a raw search query into the token list to feed
 * splitMatches(). Strips punctuation, lowercases, dedupes.
 */
export function tokensFromQuery(query) {
  if (!query) return [];
  return [
    ...new Set(
      String(query)
        .toLowerCase()
        .split(/[\s,|+&/\-()._:;'"!?]+/)
        .filter(Boolean),
    ),
  ];
}
